import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { fetchInvitePreview, sharingApi, InvitePreview } from '../api/sharing';
import { formatApiError } from '../utils/errorUtils';
import { useAuth } from '../contexts/AuthContext';
import { documentsApi } from '../api/documents';
import { cryptoApi } from '../api/crypto';
import {
  b64,
  importPublicJwk,
  unwrapDekBytesForLink,
  wrapDekBytesForUser,
} from '../utils/e2eeCrypto';
import { clearSharedMemberAccessCache } from '../utils/useSharedMemberAccess';

const ShareAccept: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [preview, setPreview] = useState<InvitePreview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setError('Link invalid.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const p = await fetchInvitePreview(token);
        if (!cancelled) setPreview(p);
      } catch (e: any) {
        if (!cancelled) setError(formatApiError(e.response?.data?.detail) || 'Invitație negăsită sau expirată.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;
    if (!user) return;
    setAccepting(true);
    setError('');
    setSyncMsg('');
    try {
      await sharingApi.acceptInvite(token);

      // ---- Materialise per-file user envelopes from the link envelopes
      // that the owner set up for this token. We hold the raw token
      // (it's in the URL), the owner-side flow only stored the hash
      // server-side. For every (document, file) we unwrap the DEK
      // with the link key, then re-wrap it for our own RSA public
      // key. Once that succeeds we ask the server to drop the
      // matching link envelopes so an attacker who later steals the
      // share URL gets nothing useful.
      const myKeys = await cryptoApi.getMyKeys();
      if (!myKeys) throw new Error('Cheile E2EE lipsesc pentru contul tău.');
      const myPub = await importPublicJwk(JSON.parse(myKeys.public_jwk));

      const collectMemberIds = async (): Promise<number[]> => {
        if (preview?.invite_kind === 'member' && preview.member_id) {
          return [preview.member_id];
        }
        if (preview?.invite_kind === 'family' && preview.family_id) {
          const { familiesApi } = await import('../api/families');
          const members = await familiesApi.getMembers(preview.family_id);
          return members.map((m) => m.id);
        }
        return [];
      };

      const memberIds = await collectMemberIds();
      let processed = 0;
      let skipped = 0;
      for (const mid of memberIds) {
        const idsResp = await documentsApi.listMemberE2eeIds(mid);
        for (const doc of idsResp.documents || []) {
          try {
            const linkMeta = await documentsApi.getE2eeLinkMetadata(doc.id, token);
            const filesToUpsert: Array<{
              file_id: number;
              user_id: number;
              wrapped_dek: string;
            }> = [];
            for (const file of linkMeta.files) {
              try {
                // The owner-side flow packed {iv ‖ ciphertext} into a
                // single base64 field; split the 12-byte IV back off.
                const packed = b64.b64ToBytes(file.wrapped_dek);
                if (packed.length < 13) throw new Error('Link envelope too short');
                const iv = packed.slice(0, 12);
                const ciphertext = packed.slice(12);
                const dekBytes = await unwrapDekBytesForLink(token, ciphertext, iv);
                const wrapped = await wrapDekBytesForUser(myPub, dekBytes);
                filesToUpsert.push({
                  file_id: file.file_id,
                  user_id: user.id,
                  wrapped_dek: b64.bytesToB64(wrapped),
                });
              } catch {
                skipped++;
              }
            }
            if (filesToUpsert.length > 0) {
              await documentsApi.upsertE2eeEnvelope(doc.id, { files: filesToUpsert });
              await documentsApi.deleteE2eeLinkEnvelope(doc.id, {
                token,
                file_ids: filesToUpsert.map((f) => f.file_id),
              });
              processed += filesToUpsert.length;
            }
          } catch {
            skipped += doc.file_count || 1;
          }
        }
      }

      if (processed === 0 && skipped === 0) {
        setSyncMsg('Invitație acceptată. Nu existau documente criptate de partajat.');
      } else if (skipped === 0) {
        setSyncMsg(`Chei pregătite pentru ${processed} fișier${processed === 1 ? '' : 'e'}.`);
      } else {
        setSyncMsg(
          `Chei pregătite: ${processed} reușite, ${skipped} omise. ` +
            'Cere proprietarului familiei să reîmpacheteze cheile pentru contul tău.'
        );
      }
      setAccepted(true);
      // Invalidate the inbound-member cache so the next mount of any
      // page that uses `useViewedAsSharedMember` picks up the freshly
      // accepted share. Otherwise the cached set would still be empty
      // for the rest of the session and family-link redirects would
      // skip the new member.
      clearSharedMemberAccessCache();
      // Member-only invites land on `/shared-members` (the family
      // page is off-limits to them); family invites land on
      // `/families` as before.
      const destination =
        preview?.invite_kind === 'member' ? '/shared-members' : '/families';
      setTimeout(() => navigate(destination), 1800);
    } catch (e: any) {
      setError(formatApiError(e.response?.data?.detail) || 'Acceptarea a eșuat.');
    } finally {
      setAccepting(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="page-container" style={{ paddingTop: 48 }}>
        <div className="skeleton skeleton-card" style={{ maxWidth: 480, margin: '0 auto' }} />
      </div>
    );
  }

  return (
    <div className="page-container admin-page" style={{ maxWidth: 520, margin: '0 auto', paddingTop: 48 }}>
      <h1 className="page-title" style={{ fontSize: '1.5rem', marginBottom: 16 }}>
        Invitație de partajare
      </h1>
      {error && !preview && <div className="alert alert-error">{error}</div>}
      {preview && (
        <>
          {preview.status !== 'pending' && (
            <div className="alert alert-error">Această invitație nu mai este activă ({preview.status}).</div>
          )}
          {preview.status === 'pending' && (
            <>
              <div className="card" style={{ marginBottom: 24 }}>
                <p style={{ margin: 0, fontSize: 'var(--text-sm)', color: 'var(--color-slate-700)' }}>
                  <strong>{preview.invite_kind === 'family' ? 'Familie' : 'Membru'}:</strong> {preview.resource_name}
                </p>
                {preview.inviter_display && (
                  <p style={{ margin: '12px 0 0', fontSize: 'var(--text-sm)', color: 'var(--color-slate-600)' }}>
                    Invitat de: {preview.inviter_display}
                  </p>
                )}
                <p style={{ margin: '12px 0 0', fontSize: 'var(--text-xs)', color: 'var(--color-slate-500)' }}>
                  Expiră: {new Date(preview.expires_at).toLocaleString('ro-RO')}
                </p>
              </div>
              {error && <div className="alert alert-error">{error}</div>}
              {syncMsg && <div className="alert alert-success">{syncMsg}</div>}
              {accepted && (
                <div className="alert alert-success">
                  {preview?.invite_kind === 'member'
                    ? 'Acceptat! Redirecționare către membrii partajați…'
                    : 'Acceptat! Redirecționare către familii…'}
                </div>
              )}
              {!accepted && !user && (
                <p style={{ fontSize: 'var(--text-sm)', marginBottom: 16 }}>
                  <Link to={`/login?next=/share/${encodeURIComponent(token || '')}`}>Autentifică-te</Link>
                  {' '}pentru a accepta invitația.
                </p>
              )}
              {!accepted && user && preview.status === 'pending' && (
                <button type="button" className="btn btn-primary" onClick={handleAccept} disabled={accepting}>
                  {accepting ? 'Se acceptă…' : 'Acceptă invitația'}
                </button>
              )}
            </>
          )}
        </>
      )}
      <p style={{ marginTop: 24 }}>
        <Link to="/">Înapoi la pagina principală</Link>
      </p>
    </div>
  );
};

export default ShareAccept;
